// src/hooks/useRequisitions.ts
//
// react-query (v3) hooks for the internal Requisition feature
// (EE-207). Wraps util/network/Requisitions.ts with a central query
// key factory so list/detail invalidation stays consistent across every
// action mutation.
import {
  useMutation,
  UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import {
  CreateRequisitionType,
  EditableRequisitionFieldsType,
  RequisitionListFiltersType,
  RequisitionItemPrefillType,
  RequisitionListType,
  RequisitionOptionsType,
  RequisitionPartStatusType,
  RequisitionType,
  UpdateRequisitionType,
} from "types/RequisitionTypes";
import {
  apiAcknowledgeRequisitionStatus,
  apiCountRequisitionStatusUpdates,
  apiCloseRequisition,
  apiCancelRequisition,
  apiCancelRequisitionLine,
  apiCreateRequisition,
  apiDeleteDraftRequisition,
  apiGetRequisition,
  apiGetRequisitionItemPrefill,
  apiGetRequisitionOptions,
  apiGetRequisitionPartStatus,
  apiGetRequisitions,
  apiRejectRequisition,
  apiSubmitRequisition,
  apiUpdateRequisition,
  apiUpdateRequisitionEditableFields,
  RequisitionItemPrefillRef,
} from "util/network/Requisitions";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const requisitionKeys = {
  all: ["requisitions"] as const,
  options: () => [...requisitionKeys.all, "options"] as const,
  lists: () => [...requisitionKeys.all, "list"] as const,
  list: (filters: RequisitionListFiltersType) =>
    [...requisitionKeys.lists(), filters] as const,
  details: () => [...requisitionKeys.all, "detail"] as const,
  statusUpdates: () => [...requisitionKeys.all, "statusUpdates"] as const,
  detail: (id: number) => [...requisitionKeys.details(), id] as const,
  partStatus: (orderItemId: number) =>
    [...requisitionKeys.all, "partStatus", orderItemId] as const,
  itemPrefill: (ref: RequisitionItemPrefillRef) =>
    [
      ...requisitionKeys.all,
      "itemPrefill",
      ref.orderItemId ?? null,
      ref.quoteItemId ?? null,
    ] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

// Dropdown/config option lists (types, purposes, materialTypes, profileTypes,
// toolCategories, cancellationReasons, cuttingAllowanceMm). Long staleTime -
// this is DB-seeded reference data that essentially never changes at runtime.
export const useRequisitionOptions = (
  token: string,
  options?: UseQueryOptions<RequisitionOptionsType, any>
) =>
  useQuery<RequisitionOptionsType, any>(
    requisitionKeys.options(),
    () => apiGetRequisitionOptions(token),
    {
      staleTime: 30 * 60 * 1000,
      enabled: !!token,
      ...options,
    }
  );

// Dashboard list (#12) - scope + filters + server pagination/sort.
export const useRequisitionList = (
  token: string,
  filters: RequisitionListFiltersType = {},
  options?: UseQueryOptions<RequisitionListType, any>
) =>
  useQuery<RequisitionListType, any>(
    requisitionKeys.list(filters),
    () => apiGetRequisitions(token, filters),
    {
      keepPreviousData: true,
      enabled: !!token,
      ...options,
    }
  );

// Single requisition, including items + full audit trail.
export const useRequisition = (
  token: string,
  id: number | undefined,
  options?: UseQueryOptions<RequisitionType, any>
) =>
  useQuery<RequisitionType, any>(
    requisitionKeys.detail(id as number),
    () => apiGetRequisition(token, id as number),
    {
      enabled: !!token && id !== undefined,
      ...options,
    }
  );

// Raw-material item values derived from the Sales Order or quote part's
// product (#12) - material, certificate flag, raw-stock dimensions, quantity.
// Product data for a quoted or ordered part doesn't change, so this is cached
// for the session rather than refetched every time the form reopens.
export const useRequisitionItemPrefill = (
  token: string,
  ref: RequisitionItemPrefillRef | undefined,
  options?: UseQueryOptions<RequisitionItemPrefillType, any>
) =>
  useQuery<RequisitionItemPrefillType, any>(
    requisitionKeys.itemPrefill(ref ?? {}),
    () => apiGetRequisitionItemPrefill(token, ref as RequisitionItemPrefillRef),
    {
      enabled:
        !!token &&
        ref !== undefined &&
        (ref.orderItemId !== undefined || ref.quoteItemId !== undefined),
      staleTime: 10 * 60 * 1000,
      retry: false,
      ...options,
    }
  );

// One prefill per picked part, for a material requisition raised against
// several parts of one order - each gets its own material line. Same cache
// entries as the single-ref hook above, so a part prefilled once is not
// fetched again when it is re-picked. Returns the results in the order of
// the refs; a ref whose prefill has not landed yet is undefined.
export const useRequisitionItemPrefills = (
  token: string,
  refs: RequisitionItemPrefillRef[],
  enabled: boolean = true
): (RequisitionItemPrefillType | undefined)[] => {
  const results = useQueries(
    refs.map((ref) => ({
      queryKey: requisitionKeys.itemPrefill(ref),
      queryFn: () => apiGetRequisitionItemPrefill(token, ref),
      enabled:
        enabled &&
        !!token &&
        (ref.orderItemId !== undefined || ref.quoteItemId !== undefined),
      staleTime: 10 * 60 * 1000,
      retry: false,
    }))
  );
  return refs.map(
    (_, index) => results[index]?.data as RequisitionItemPrefillType | undefined
  );
};

// Part-level "active requisition exists" indicator (#14/#18) for a Sales
// Order part (order item id).
export const useRequisitionPartStatus = (
  token: string,
  orderItemId: number | undefined,
  options?: UseQueryOptions<RequisitionPartStatusType, any>
) =>
  useQuery<RequisitionPartStatusType, any>(
    requisitionKeys.partStatus(orderItemId as number),
    () => apiGetRequisitionPartStatus(token, orderItemId as number),
    {
      enabled: !!token && orderItemId !== undefined,
      ...options,
    }
  );

// Batched part-status lookup (#18) for a whole list of order items at once -
// e.g. every manufactured part row in a Sales Order view. The backend's
// GET /requisitions/part-status endpoint
// (RequisitionPartController.get_active_requisitions_for_part) only
// takes a single orderItemId per call, there is no multi-id variant to call
// instead. To still keep this "one call per list render" rather than each
// row mounting its own independent useRequisitionPartStatus hook on
// its own schedule, the list/table component calls this ONE hook with every
// orderItemId it's about to render; react-query then fans the underlying
// per-id requests out in parallel (via useQueries) and caches them under the
// same partStatus() keys the single-id hook uses, so re-visiting a part
// already fetched elsewhere in the app is free. Returns a plain map so
// callers don't need to know react-query's per-query result shape.
// `enabled` lets a caller skip the whole fan-out - the endpoint is
// MANAGER-gated, so for a non-manager every one of these N requests is a
// guaranteed 401 and there is no indicator to show anyway.
export const useRequisitionPartStatuses = (
  token: string,
  orderItemIds: number[],
  enabled: boolean = true
): Record<number, RequisitionPartStatusType> => {
  const results = useQueries(
    orderItemIds.map((orderItemId) => ({
      queryKey: requisitionKeys.partStatus(orderItemId),
      queryFn: () => apiGetRequisitionPartStatus(token, orderItemId),
      enabled: enabled && !!token,
      staleTime: 60 * 1000,
      // A 401 here (non-manager) or a 404 is not worth three retries per part -
      // on a large order that would be N x 4 doomed requests per page load.
      retry: false,
    }))
  );

  // No useMemo: both `results` and the caller's id array are fresh objects on
  // every render, so a memo here can never hit - it would only add work.
  const map: Record<number, RequisitionPartStatusType> = {};
  orderItemIds.forEach((orderItemId, index) => {
    const data = results[index]?.data as RequisitionPartStatusType | undefined;
    if (data) map[orderItemId] = data;
  });
  return map;
};

// ---------------------------------------------------------------------------
// Mutations
//
// Every action mutation invalidates the list query set plus (when the
// affected id is known) the single-detail query, so a component only needs
// to call the mutation and doesn't have to remember cache bookkeeping.
// ---------------------------------------------------------------------------

// Every Sales Order part a requisition is raised against: the header's plus
// each line's. A requisition covers several parts of one order, and each of
// them carries its own part-status indicator.
const partIdsOf = (
  requisition: Pick<RequisitionType, "order_item_id" | "items">
): (number | null | undefined)[] => [
  requisition.order_item_id,
  ...(requisition.items ?? []).map((item) => item.order_item_id),
];

const useInvalidateRequisitions = () => {
  const queryClient = useQueryClient();
  return (id?: number, orderItemIds?: (number | null | undefined)[]) => {
    queryClient.invalidateQueries(requisitionKeys.lists());
    if (id !== undefined) {
      queryClient.invalidateQueries(requisitionKeys.detail(id));
    }
    // #18 - any workflow action can flip hasActiveRequisition for the linked
    // Sales Order parts (most obviously advance-to-CLOSED and cancel, but
    // there's no harm invalidating unconditionally here too - part-status has
    // a short staleTime anyway). Without this the SO Part List's red
    // indicator would only clear on its own 60s staleTime instead of
    // immediately after the action that closed the requisition.
    new Set(
      (orderItemIds ?? []).filter(
        (orderItemId): orderItemId is number =>
          orderItemId !== undefined && orderItemId !== null
      )
    ).forEach((orderItemId) => {
      queryClient.invalidateQueries(requisitionKeys.partStatus(orderItemId));
    });
  };
};

export const useCreateRequisition = (
  token: string,
  options?: UseMutationOptions<
    RequisitionType,
    any,
    CreateRequisitionType
  >
) => {
  const invalidate = useInvalidateRequisitions();
  return useMutation<RequisitionType, any, CreateRequisitionType>(
    (requisition) => apiCreateRequisition(token, requisition),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // A new Draft is itself an ACTIVE_STATUSES member (#18), so its
        // linked part immediately needs its red indicator invalidated too.
        invalidate(undefined, partIdsOf(data));
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useUpdateRequisition = (
  token: string,
  options?: UseMutationOptions<
    RequisitionType,
    any,
    { id: number; requisition: UpdateRequisitionType }
  >
) => {
  const invalidate = useInvalidateRequisitions();
  return useMutation<
    RequisitionType,
    any,
    { id: number; requisition: UpdateRequisitionType }
  >(({ id, requisition }) => apiUpdateRequisition(token, id, requisition), {
    ...options,
    onSuccess: (data, variables, context) => {
      invalidate(data.id, partIdsOf(data));
      options?.onSuccess?.(data, variables, context);
    },
  });
};

// Draft-only (409 if the requisition has left Draft).
//
// Takes `orderItemIds` alongside the id because DELETE returns a bare boolean -
// there's no response body to read the linked parts off of, and deleting the
// only active Draft on a part is exactly the case that must clear that part's
// red indicator (#18). Callers pass the header's and every line's
// order_item_id.
export const useDeleteDraftRequisition = (
  token: string,
  options?: UseMutationOptions<
    boolean,
    any,
    { id: number; orderItemIds?: (number | null | undefined)[] }
  >
) => {
  const invalidate = useInvalidateRequisitions();
  return useMutation<
    boolean,
    any,
    { id: number; orderItemIds?: (number | null | undefined)[] }
  >(({ id }) => apiDeleteDraftRequisition(token, id), {
    ...options,
    onSuccess: (data, variables, context) => {
      invalidate(variables.id, variables.orderItemIds);
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useSubmitRequisition = (
  token: string,
  options?: UseMutationOptions<RequisitionType, any, number>
) => {
  const invalidate = useInvalidateRequisitions();
  return useMutation<RequisitionType, any, number>(
    (id) => apiSubmitRequisition(token, id),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidate(data.id, partIdsOf(data));
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

// No approve hook (EE-253): approving is an Inquiry transition, so it goes
// through the Inquiry mutations. The requisition follows via the backend sync,
// which is why those mutations invalidate the requisition queries too.

// `comment` is mandatory for reject (enforced server-side, 400 if missing).
export const useRejectRequisition = (
  token: string,
  options?: UseMutationOptions<
    RequisitionType,
    any,
    { id: number; comment: string }
  >
) => {
  const invalidate = useInvalidateRequisitions();
  return useMutation<
    RequisitionType,
    any,
    { id: number; comment: string }
  >(({ id, comment }) => apiRejectRequisition(token, id, comment), {
    ...options,
    onSuccess: (data, variables, context) => {
      invalidate(data.id, partIdsOf(data));
      options?.onSuccess?.(data, variables, context);
    },
  });
};

// Ready to Use -> Closed. The only requisition-driven step at the end of the
// flow, and the one that also closes the linked Inquiry (EE-253).
export const useCloseRequisition = (
  token: string,
  options?: UseMutationOptions<
    RequisitionType,
    any,
    { id: number; comment?: string }
  >
) => {
  const invalidate = useInvalidateRequisitions();
  return useMutation<
    RequisitionType,
    any,
    { id: number; comment?: string }
  >(({ id, comment }) => apiCloseRequisition(token, id, comment), {
    ...options,
    onSuccess: (data, variables, context) => {
      // Close moves the requisition to its terminal status, which is the main
      // reason part-status invalidation matters here (#18).
      invalidate(data.id, partIdsOf(data));
      options?.onSuccess?.(data, variables, context);
    },
  });
};

// `reasonCode` is mandatory (400 if missing/unknown); `comment` is mandatory
// when the seeded reason has requiresComment=true.
export const useCancelRequisition = (
  token: string,
  options?: UseMutationOptions<
    RequisitionType,
    any,
    { id: number; reasonCode: string; comment?: string }
  >
) => {
  const invalidate = useInvalidateRequisitions();
  return useMutation<
    RequisitionType,
    any,
    { id: number; reasonCode: string; comment?: string }
  >(
    ({ id, reasonCode, comment }) =>
      apiCancelRequisition(token, id, reasonCode, comment),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // Cancel always lands on CLOSED - the other path (besides advance)
        // that can flip a part's hasActiveRequisition flag off (#18).
        invalidate(data.id, partIdsOf(data));
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

export const useCancelRequisitionLine = (
  token: string,
  options?: UseMutationOptions<
    RequisitionType,
    any,
    { id: number; lineId: number; reasonCode: string; comment?: string }
  >
) => {
  const invalidate = useInvalidateRequisitions();
  const queryClient = useQueryClient();
  return useMutation<
    RequisitionType,
    any,
    { id: number; lineId: number; reasonCode: string; comment?: string }
  >(
    ({ id, lineId, reasonCode, comment }) =>
      apiCancelRequisitionLine(token, id, lineId, reasonCode, comment),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidate(data.id, partIdsOf(data));
        // The line left the pool or an Inquiry, and the order's Purchasing
        // panel shows it cancelled. (The pool is under requisitionKeys.all.)
        queryClient.invalidateQueries(["inquiries"]);
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "orders" && query.queryKey[2] === "purchasing",
        });
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};

// The number on the Requisitions menu badge: requisitions with a status
// update nobody has opened yet. One COUNT on the server, polled - the badge
// is on every page, so it must stay cheap. Anything that clears or raises
// the flag invalidates requisitionKeys.all or the key directly.
export const useRequisitionStatusUpdateCount = (
  token: string,
  options?: UseQueryOptions<{ count: number }, any>
) =>
  useQuery<{ count: number }, any>(
    requisitionKeys.statusUpdates(),
    () => apiCountRequisitionStatusUpdates(token),
    {
      enabled: !!token,
      refetchInterval: 60 * 1000,
      refetchOnWindowFocus: true,
      ...options,
    }
  );

// Clears the unacknowledged-status indicator (EE-253). Fired when the detail
// dialog opens, not from a click on the indicator itself.
//
// Optimistic: the marker leaves the row, and the badge count drops, the moment
// the dialog opens; a failed request puts both back. Nothing is refetched to
// show it - a refetch swaps the whole table for skeletons, and all that
// changed is one flag on one row. The count is re-read afterwards to settle
// what the guess got wrong (another row acknowledged elsewhere, say), which
// redraws only the badge.
//
// The detail cache takes the flag too, so the dialog's own effect does not see
// it pending and fire again. The detail is not refetched: that would re-read
// the requisition the user is reading, for a field they cannot see.
type AckSnapshot = {
  lists: [unknown, RequisitionListType | undefined][];
  detail: RequisitionType | undefined;
  count: { count: number } | undefined;
};

export const useAcknowledgeRequisitionStatus = (token: string) => {
  const queryClient = useQueryClient();
  return useMutation<RequisitionType, any, number, AckSnapshot>(
    (id) => apiAcknowledgeRequisitionStatus(token, id),
    {
      onMutate: async (id) => {
        // An in-flight read would land after the guess and undo it.
        await Promise.all([
          queryClient.cancelQueries(requisitionKeys.lists()),
          queryClient.cancelQueries(requisitionKeys.statusUpdates()),
        ]);
        const snapshot: AckSnapshot = {
          lists: queryClient.getQueriesData<RequisitionListType | undefined>(
            requisitionKeys.lists()
          ),
          detail: queryClient.getQueryData<RequisitionType>(
            requisitionKeys.detail(id)
          ),
          count: queryClient.getQueryData<{ count: number }>(
            requisitionKeys.statusUpdates()
          ),
        };
        queryClient.setQueriesData<RequisitionListType | undefined>(
          requisitionKeys.lists(),
          (list) =>
            list && {
              ...list,
              items: list.items.map((row) =>
                row.id === id ? { ...row, status_ack_pending: false } : row
              ),
            }
        );
        if (snapshot.detail) {
          queryClient.setQueryData(requisitionKeys.detail(id), {
            ...snapshot.detail,
            status_ack_pending: false,
          });
        }
        if (snapshot.count) {
          queryClient.setQueryData(requisitionKeys.statusUpdates(), {
            count: Math.max(0, snapshot.count.count - 1),
          });
        }
        return snapshot;
      },
      onError: (_error, id, snapshot) => {
        if (!snapshot) return;
        snapshot.lists.forEach(([key, list]) =>
          queryClient.setQueryData(key as any, list)
        );
        if (snapshot.detail) {
          queryClient.setQueryData(requisitionKeys.detail(id), snapshot.detail);
        }
        if (snapshot.count) {
          queryClient.setQueryData(requisitionKeys.statusUpdates(), snapshot.count);
        }
      },
      onSuccess: (data) => {
        queryClient.setQueryData(requisitionKeys.detail(data.id), data);
      },
      onSettled: () => {
        queryClient.invalidateQueries(requisitionKeys.statusUpdates());
      },
    }
  );
};

// Edit-after-submit (#9/#15): only estimatedDeliveryDate/supplierId/comments.
export const useUpdateRequisitionEditableFields = (
  token: string,
  options?: UseMutationOptions<
    RequisitionType,
    any,
    { id: number; changes: EditableRequisitionFieldsType }
  >
) => {
  const invalidate = useInvalidateRequisitions();
  return useMutation<
    RequisitionType,
    any,
    { id: number; changes: EditableRequisitionFieldsType }
  >(
    ({ id, changes }) => apiUpdateRequisitionEditableFields(token, id, changes),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        invalidate(data.id);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};
